/* ============================================================
   LooksGalleryBlock — CMS card component for the Looks section
   Renders a horizontal-scroll row of look cards from service_recipes.
   Supports configurable heading, subheading, and maxItems via block data.
   ============================================================ */

import { useState, useEffect, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTenant } from '@/contexts/TenantProvider'
import { useServiceRecipes, useRecipeImages } from '@/hooks'
import type { ServiceRecipe, RecipeImage } from '@/types/database.types'
import type { Json } from '@/types/database.types'
import type { Look } from '@/types/look'
import LucideGlyph from '@/components/ui/LucideGlyph'

interface PricingRuleJson {
  tag?: string
  label?: string
}

function extractTags(variableLogic: Json): string[] {
  if (!Array.isArray(variableLogic)) return []
  return (variableLogic as PricingRuleJson[])
    .map((r) => r?.label ?? r?.tag ?? '')
    .filter(Boolean)
}

function recipeToLook(recipe: ServiceRecipe, images: RecipeImage[]): Look {
  const primary = images.find((i) => i.is_primary) ?? images[0]
  return {
    id: recipe.id,
    imageUrl:
      primary?.image_url ??
      `https://placehold.co/300x400/1a1a1a/ffffff?text=${encodeURIComponent(recipe.name)}`,
    title: recipe.name,
    description: `${recipe.base_duration} min · from $${recipe.base_price}`,
    creatorName: 'Our Team',
    startingPrice: recipe.base_price,
    durationMinutes: recipe.base_duration,
    recipeServiceIds: [recipe.id],
    tags: extractTags(recipe.variable_logic),
  }
}

/** Silent image loader — fetches images for a recipe then calls onLoaded */
function RecipeImageLoader({
  recipe,
  onLoaded,
}: {
  recipe: ServiceRecipe
  onLoaded: (look: Look) => void
}) {
  const { data: images = [] } = useRecipeImages(recipe.id)
  useEffect(() => {
    onLoaded(recipeToLook(recipe, images))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images])
  return null
}

interface LooksGalleryBlockProps {
  data: Record<string, string>
  onUpdateData?: (updates: Record<string, string>) => void
}

export default function LooksGalleryBlock({ data }: LooksGalleryBlockProps) {
  const { slug } = useParams<{ slug: string }>()
  const { tenant } = useTenant()
  const tenantId = tenant?.id

  const heading = data.heading || 'Browse Looks'
  const subheading = data.subheading || ''
  const maxItems = parseInt(data.maxItems || '6', 10)

  const { data: recipes = [], isLoading } = useServiceRecipes(tenantId)
  const preview = recipes.slice(0, maxItems)

  const [looksMap, setLooksMap] = useState<Record<string, Look>>({})
  const handleLookLoaded = useCallback((look: Look) => {
    setLooksMap((prev) => {
      if (prev[look.id]?.imageUrl === look.imageUrl) return prev
      return { ...prev, [look.id]: look }
    })
  }, [])

  const looks = preview.map((r) => looksMap[r.id]).filter(Boolean) as Look[]

  // Determine if we're in a state where recipes simply aren't available
  const hasNoData = !tenantId || (!isLoading && recipes.length === 0)

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8" style={{ minHeight: 220 }}>
      {/* Silent image loaders */}
      {preview.map((recipe) => (
        <RecipeImageLoader key={recipe.id} recipe={recipe} onLoaded={handleLookLoaded} />
      ))}

      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8 flex items-end justify-between">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {heading}
          </h2>
          {subheading && (
            <p className="mt-2 text-white/50 text-sm sm:text-base max-w-md">
              {subheading}
            </p>
          )}
        </div>
        {slug && (
          <Link
            to={`/${slug}/looks`}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-origin-accent hover:text-white transition-colors"
          >
            View All Looks
            <LucideGlyph name="arrow_forward" size={18} />
          </Link>
        )}
      </div>

      {/* Horizontal scroll cards */}
      <div className="max-w-6xl mx-auto">
        {hasNoData ? (
          /* ── Empty / placeholder state ── */
          <div className="flex flex-col items-center justify-center py-12 rounded-2xl border border-dashed border-white/10 bg-white/[0.02]">
            <div className="text-4xl mb-3 opacity-30">💇</div>
            <p className="text-white/40 text-sm font-medium mb-1">Looks Gallery</p>
            <p className="text-white/20 text-xs max-w-xs text-center">
              {!tenantId
                ? 'Connect to a tenant to load looks from service recipes'
                : 'No service recipes found — add recipes in the Recipes admin page to populate this gallery'}
            </p>
          </div>
        ) : isLoading || looks.length === 0 ? (
          /* ── Skeleton loading ── */
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4">
            {Array.from({ length: Math.min(maxItems, 4) }).map((_, i) => (
              <div
                key={i}
                className="flex-none w-[260px] sm:w-[300px] rounded-2xl aspect-[3/4] animate-pulse"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.04)' }}
              />
            ))}
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
            {looks.map((look) => (
              <Link
                key={look.id}
                to={`/${slug}/looks`}
                data-keep-white
                className="group relative flex-none w-[260px] sm:w-[300px] snap-start rounded-2xl overflow-hidden aspect-[3/4] bg-white/5"
              >
                <img
                  src={look.imageUrl}
                  alt={look.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-semibold text-lg leading-tight">{look.title}</p>
                  <p className="text-white/60 text-sm mt-1">{look.creatorName}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-white/70 bg-white/10 px-2 py-0.5 rounded-full backdrop-blur-sm">
                      From ${look.startingPrice}
                    </span>
                    <span className="text-xs text-white/70 bg-white/10 px-2 py-0.5 rounded-full backdrop-blur-sm">
                      {look.durationMinutes} min
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Mobile CTA */}
      {slug && (
        <div className="sm:hidden mt-6 text-center">
          <Link
            to={`/${slug}/looks`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-origin-accent hover:text-white transition-colors"
          >
            View All Looks
            <LucideGlyph name="arrow_forward" size={18} />
          </Link>
        </div>
      )}
    </div>
  )
}
